import { useMemo, useState } from "react";
import { Upload, Trash2, AlertTriangle, FileSpreadsheet, Loader2 } from "lucide-react";
import { Modal, SelectField } from "../lib/ui";
import { api, money, humanDate } from "../lib/api";
import { CustomerCombobox } from "./Modals";

const KINDS = [
  ["customer", "Cari tahsilat", "Cari ödeme"],
  ["record", "Gelir kaydı", "Gider kaydı"],
  ["transfer", "Hesaplar arası virman", "Hesaplar arası virman"],
  ["bank", "Sadece banka hareketi", "Sadece banka hareketi"],
];

function UploadStep({ accounts, accountId, setAccountId, onParsed }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setError("Ekstre dosyası seçin.");
    setBusy(true); setError("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("account_id", accountId);
    try {
      const { data } = await api.post("/bank-import/parse", fd, { timeout: 180000 });
      onParsed(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Ekstre okunamadı");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="bank-import-upload-step">
      <SelectField testid="bank-import-account" label="Ekstrenin ait olduğu hesap" value={accountId}
                   onChange={(e) => setAccountId(e.target.value)}>
        {accounts.filter((a) => a.type !== "cash").map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </SelectField>
      <label className="bi-drop" data-testid="bank-import-drop">
        <FileSpreadsheet size={26} />
        <strong>{file ? file.name : "Ekstre dosyasını seçin"}</strong>
        <small>Excel (.xlsx / .xls), CSV, PDF veya fotoğraf · PDF ekstreler yapay zekâ ile satırlara ayrılır</small>
        <input type="file" data-testid="bank-import-file" accept=".xlsx,.xls,.csv,.txt,.pdf,.jpg,.jpeg,.png,.webp"
               onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      {error && <div className="form-error" data-testid="bank-import-error">{error}</div>}
      <div className="modal-actions">
        <button type="submit" className="primary-button" disabled={busy || !file} data-testid="bank-import-parse-btn">
          {busy ? <><Loader2 size={15} className="spin" /> Ekstre okunuyor…</> : <><Upload size={15} /> Ekstreyi oku</>}
        </button>
      </div>
    </form>
  );
}

function RowDetail({ row, set, customers, accounts, accountId }) {
  if (row.kind === "customer") return (
    <div className="bi-detail">
      <CustomerCombobox customers={customers} selectedId={row.customer_id} label="Cari"
                        selectedName={customers.find((c) => c.id === row.customer_id)?.name || ""}
                        testid={`bi-cust-${row.key}`} onSelect={(c) => set({ customer_id: c.id })} />
      <select value={row.balance_target} data-testid={`bi-target-${row.key}`} onChange={(e) => set({ balance_target: e.target.value })}>
        <option value="">Ortak bakiye</option><option value="Resmi">Resmi</option><option value="Proforma">Proforma</option>
      </select>
    </div>
  );
  if (row.kind === "transfer") return (
    <div className="bi-detail">
      <select value={row.other_account_id || ""} data-testid={`bi-other-acc-${row.key}`} onChange={(e) => set({ other_account_id: e.target.value })}>
        <option value="">{row.direction === "out" ? "Gönderilen hesap…" : "Gelen hesap…"}</option>
        {accounts.filter((a) => a.id !== accountId && !a.off_balance && (a.currency || "TRY") === "TRY").map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    </div>
  );
  return (
    <div className="bi-detail">
      <input list="bi-categories" value={row.category} placeholder="Kategori" data-testid={`bi-category-${row.key}`}
             onChange={(e) => set({ category: e.target.value })} />
    </div>
  );
}

function ImportRow({ row, set, remove, ...rest }) {
  return (
    <tr data-testid={`bi-row-${row.key}`} className={row.similar?.length ? "bi-warn" : ""}>
      <td><input type="checkbox" checked={!!row.sel} data-testid={`bi-sel-${row.key}`} onChange={(e) => set({ sel: e.target.checked })} /></td>
      <td><input type="date" value={row.date} data-testid={`bi-date-${row.key}`} onChange={(e) => set({ date: e.target.value })} /></td>
      <td className="bi-desc">
        <input value={row.description} data-testid={`bi-desc-${row.key}`} onChange={(e) => set({ description: e.target.value })} />
        {row.similar?.length > 0 && (
          <div className="bi-similar" data-testid={`bi-similar-${row.key}`}>
            <AlertTriangle size={13} />
            <span>{row.similar.some((s) => s.exact) ? "Bu satır daha önce aktarılmış:" : "Benzer kayıt var:"}{" "}
              {row.similar.map((s) => `${humanDate(s.date)} · ${s.category || ""} · ${s.description || "—"}`).join(" | ")}</span>
          </div>
        )}
      </td>
      <td className={`amount ${row.direction === "in" ? "positive" : "negative"}`} data-testid={`bi-amount-${row.key}`}>
        {row.direction === "in" ? "+" : "-"}{money(row.amount)}
      </td>
      <td>
        <select value={row.kind} data-testid={`bi-kind-${row.key}`}
                onChange={(e) => set({ kind: e.target.value, ...(e.target.value === "customer" ? { category: row.direction === "in" ? "Tahsilat" : "Ödeme" } : {}) })}>
          {KINDS.map(([k, i, o]) => <option key={k} value={k}>{row.direction === "in" ? i : o}</option>)}
        </select>
      </td>
      <td><RowDetail row={row} set={set} {...rest} /></td>
      <td><button type="button" className="icon-button" title="Listeden çıkar" data-testid={`bi-remove-${row.key}`} onClick={remove}><Trash2 size={14} /></button></td>
    </tr>
  );
}

function rowError(r) {
  if (!r.date) return "tarih";
  if (r.kind === "customer" && !r.customer_id) return "cari";
  if (r.kind === "transfer" && !r.other_account_id) return "virman hesabı";
  return "";
}

function ReviewStep({ parsed, accounts, customers, categories, onDone, onBack }) {
  const [rows, setRows] = useState(() => parsed.rows.map((r) => ({
    ...r, sel: false, customer_id: r.suggested_customer_id || "", balance_target: "", other_account_id: "",
  })));
  const [bulkKind, setBulkKind] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const setRow = (key, patch) => { setError(""); setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r))); };
  const removeRows = (pred) => { setError(""); setRows((rs) => rs.filter((r) => !pred(r))); };
  const selCount = rows.filter((r) => r.sel).length;
  const totals = useMemo(() => ({
    in: rows.filter((r) => r.direction === "in").reduce((s, r) => s + r.amount, 0),
    out: rows.filter((r) => r.direction === "out").reduce((s, r) => s + r.amount, 0),
    warn: rows.filter((r) => r.similar?.length).length,
  }), [rows]);
  const applyBulk = (k) => { setBulkKind(""); if (k) setRows((rs) => rs.map((r) => (r.sel ? { ...r, kind: k } : r))); };
  const save = async () => {
    const bad = rows.findIndex((r) => rowError(r));
    if (bad >= 0) return setError(`${bad + 1}. satırda ${rowError(rows[bad])} seçilmedi.`);
    setBusy(true); setError("");
    try {
      const payload = rows.map(({ date, amount, direction, description, kind, customer_id, balance_target, category, other_account_id, import_hash }) => ({
        date, amount, direction, description, kind, balance_target, category, import_hash,
        customer_id: kind === "customer" ? customer_id : null, other_account_id: kind === "transfer" ? other_account_id : null,
      }));
      const { data } = await api.post("/bank-import/commit", { account_id: parsed.account.id, file_name: parsed.file_name, rows: payload });
      onDone(data.created);
    } catch (err) {
      setError(err.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="bank-import-review-step">
      <div className="bi-toolbar">
        <span data-testid="bi-summary"><strong>{rows.length}</strong> satır · Giriş <strong className="positive">+{money(totals.in)}</strong> · Çıkış <strong className="negative">-{money(totals.out)}</strong>
          {totals.warn > 0 && <> · <span className="negative">{totals.warn} benzer kayıt uyarısı</span></>}</span>
        <div className="button-row">
          <button type="button" className="secondary-button" data-testid="bi-select-all"
                  onClick={() => setRows((rs) => rs.map((r) => ({ ...r, sel: selCount !== rs.length })))}>
            {selCount === rows.length && rows.length ? "Seçimi kaldır" : "Tümünü seç"}
          </button>
          <select value={bulkKind} disabled={!selCount} data-testid="bi-bulk-kind" onChange={(e) => applyBulk(e.target.value)}>
            <option value="">Seçililere tür uygula…</option>
            {KINDS.map(([k, i]) => <option key={k} value={k}>{k === "customer" ? "Cari tahsilat / ödeme" : k === "record" ? "Gelir / gider kaydı" : i}</option>)}
          </select>
          <button type="button" className="secondary-button" disabled={!selCount} data-testid="bi-remove-selected" onClick={() => removeRows((r) => r.sel)}>
            <Trash2 size={14} /> Seçilileri sil ({selCount})
          </button>
          {totals.warn > 0 && <button type="button" className="secondary-button" data-testid="bi-remove-similar" onClick={() => removeRows((r) => r.similar?.length)}>
            <AlertTriangle size={14} /> Benzerleri sil
          </button>}
        </div>
      </div>
      <datalist id="bi-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
      <div className="bi-table-wrap">
        <table className="bi-table" data-testid="bi-table">
          <thead><tr><th></th><th>Tarih</th><th>Açıklama</th><th className="amount">Tutar</th><th>Ne olarak kaydedilsin?</th><th>Detay</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <ImportRow key={r.key} row={r} set={(p) => setRow(r.key, p)} remove={() => removeRows((x) => x.key === r.key)}
                         customers={customers} accounts={accounts} accountId={parsed.account.id} />
            ))}
          </tbody>
        </table>
        {!rows.length && <div className="empty-module full"><strong>Listede satır kalmadı</strong></div>}
      </div>
      {error && <div className="form-error" data-testid="bi-error">{error}</div>}
      <div className="modal-actions">
        <button type="button" className="secondary-button" data-testid="bi-back" onClick={onBack}>Başka dosya</button>
        <button type="button" className="primary-button" disabled={busy || !rows.length} data-testid="bi-save" onClick={save}>
          {busy ? "Kaydediliyor…" : `${rows.length} satırı kaydet`}
        </button>
      </div>
    </div>
  );
}

export function BankImportModal({ onClose, onSaved, accounts, customers, transactions = [], defaultAccountId }) {
  const banks = accounts.filter((a) => a.type !== "cash");
  const [accountId, setAccountId] = useState(banks.find((a) => a.id === defaultAccountId)?.id || banks[0]?.id || "");
  const [parsed, setParsed] = useState(null);
  const [done, setDone] = useState(0);
  const categories = useMemo(() => [...new Set(["Genel", "POS Tahsilatı", "Banka Masrafı", "Kredi", "Kredi Kartı", "SGK", "Vergi",
    ...transactions.map((t) => t.category).filter(Boolean)])].sort((a, b) => a.localeCompare(b, "tr")), [transactions]);
  return (
    <Modal title="Banka ekstresi aktar" size={parsed && !done ? "full" : "md"} onClose={onClose} testid="bank-import-modal"
           subtitle={parsed ? `${parsed.account.name} · ${parsed.file_name} · aktarılan hareketler günlük rapordan gizlenir` : "Satırlar otomatik işlenmez; her kalemi siz onaylarsınız. Aktarılan hareketler günlük rapordan gizlenir."}>
      {done ? (
        <div data-testid="bi-done">
          <p>{done} hareket <strong>{parsed.account.name}</strong> hesabına kaydedildi.</p>
          <div className="modal-actions"><button className="primary-button" data-testid="bi-close" onClick={onClose}>Kapat</button></div>
        </div>
      ) : parsed ? (
        <ReviewStep parsed={parsed} accounts={accounts} customers={customers} categories={categories}
                    onBack={() => setParsed(null)} onDone={(n) => { setDone(n); onSaved(); }} />
      ) : (
        <UploadStep accounts={accounts} accountId={accountId} setAccountId={setAccountId} onParsed={setParsed} />
      )}
    </Modal>
  );
}
